import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Quiz,
  Question,
  quizService,
  authService,
  inventoryService,
  pvpService,
  type SoloQuizStartResponse,
  SOLO_QUICK_MATCH_ROUTE_ID,
  SOLO_QUICK_MATCH_PLACEHOLDER_QUIZ_ID,
} from '../services/api';
import { shouldPromptXpPotionReminder } from '../utils/inventoryXpPotions';
import {
  inventorySlotsHaveUnusedHealthPotion,
  shouldPromptHealthPotionReminder,
} from '../utils/inventoryHealthPotions';
import XpPotionReminderModal from './XpPotionReminderModal';
import HealthPotionReminderModal from './HealthPotionReminderModal';
import LivesGateModal from './LivesGateModal';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { isPwaStandalone } from '../utils/pwaStandalone';
import WitcQuizPlayer from './WitcQuizPlayer';
import McQuizPlayer from './McQuizPlayer';
import StopQuizPlayer from './StopQuizPlayer';
import GeniusQuizPlayer from './GeniusQuizPlayer';
import TileMatchQuizPlayer from './TileMatchQuizPlayer';
import WordConnectQuizPlayer from './WordConnectQuizPlayer';
import type { GeniusPlayStart, TileMatchPlayStart, WordConnectPlayStart } from '../services/api';
import {
  GENIUS_TOTAL_ROUNDS,
  isGeniusQuizFormat,
  parseGeniusPlayStart,
} from '../utils/geniusQuizFormat';
import { isTileMatchQuizFormat, parseTileMatchPlayStart } from '../utils/tileMatchQuizFormat';
import { isWordConnectQuizFormat, parseWordConnectPlayStart } from '../utils/wordConnectQuizFormat';
import { TILE_MATCH_TOTAL_ROUNDS } from '../utils/tileMatchPlayScoring';
import QuickMatchMixedPlayer from './QuickMatchMixedPlayer';
import { getQuizFullDisplayCaption } from '../utils/quizDisplayTitle';
import { isQuizLockedApiError } from '../utils/quizLockedApiError';
import { isInsufficientLivesMessage, translateLivesOrRaw } from '../utils/livesErrorI18n';
import { isQuizCareerLocked } from '../utils/quizCareerLock';
import QuizLockedModal from './QuizLockedModal';
import QuizPlayerSeo from './QuizPlayerSeo';
import QuizSoundToggleButton from './QuizSoundToggleButton';
import NavbarMusicToggle from './NavbarMusicToggle';
import { careerHomePathWithSearch } from '../constants/careerSearchParams';
import { normalizePlayDifficultyLabel } from '../utils/quizPlayBandScoring';
import {
  buildSoloArenaHostCompleteBody,
  type SoloArenaPublishSnapshot,
} from '../utils/soloArenaPublishLayout';

import './shared/QuizStyles.css';
import './QuizPlayer.css';

interface QuizPlayerProps {
  quizId: string;
}

const QuizPlayer: React.FC<QuizPlayerProps> = ({ quizId }) => {
  const { t, i18n } = useSafeTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  /** Start / load failures — shown in modal (not full-page). */
  const [startErrorModal, setStartErrorModal] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [witcPlaySessionId, setWitcPlaySessionId] = useState<string | null>(null);
  const [witcEngineBootstrapCipher, setWitcEngineBootstrapCipher] = useState<string | null>(null);
  const [stopEngineBootstrapCipher, setStopEngineBootstrapCipher] = useState<string | null>(null);
  const [mcPlaySessionId, setMcPlaySessionId] = useState<string | null>(null);
  const [mcEngineBootstrapCipher, setMcEngineBootstrapCipher] = useState<string | null>(null);
  const [geniusStart, setGeniusStart] = useState<GeniusPlayStart | null>(null);
  const [tileMatchStart, setTileMatchStart] = useState<TileMatchPlayStart | null>(null);
  const [wordConnectStart, setWordConnectStart] = useState<WordConnectPlayStart | null>(null);
  /** Band for this run — must be sent on save when the quiz supports multiple play modes. */
  const [soloPlayDifficulty, setSoloPlayDifficulty] = useState<string | null>(null);
  const [soloQuickMatch, setSoloQuickMatch] = useState(false);
  const [playDifficultiesPerQuestion, setPlayDifficultiesPerQuestion] = useState<string[] | null>(null);
  /** Increment to remount WitcQuizPlayer after POST /start (fresh client state). */
  const [witcSessionKey, setWitcSessionKey] = useState(0);
  /** Lives / API error when restarting solo from results modal (does not replace initial route guard). */
  const [soloRestartLivesMessage, setSoloRestartLivesMessage] = useState<string | null>(null);
  const [soloRestartLockedQuiz, setSoloRestartLockedQuiz] = useState<Quiz | null>(null);
  const [soloPlayAgainBusy, setSoloPlayAgainBusy] = useState(false);
  /** Lobby-style reminders before POST /start when tapping Play Again on results */
  const [soloPlayAgainXpGateOpen, setSoloPlayAgainXpGateOpen] = useState(false);
  const [soloPlayAgainHealthGateOpen, setSoloPlayAgainHealthGateOpen] = useState(false);
  /** Solo MC ≥70% accuracy: publish same run to Arena PvP when leaving results (checkbox). */
  const [arenaPublishChecked, setArenaPublishChecked] = useState(true);
  const soloArenaSnapshotRef = useRef<SoloArenaPublishSnapshot | null>(null);
  const arenaPublishCheckedRef = useRef(true);

  useEffect(() => {
    arenaPublishCheckedRef.current = arenaPublishChecked;
  }, [arenaPublishChecked]);

  const executeArenaPublishFromSoloIfNeeded = useCallback(async () => {
    const snap = soloArenaSnapshotRef.current;
    if (!snap || !arenaPublishCheckedRef.current) return;
    const qidForGate = snap.quizId?.trim();
    try {
      const summaryFresh = await pvpService.getHostedOpenAsyncSummary();
      if (summaryFresh.openCount >= summaryFresh.maxOpenRooms) return;
      if (
        qidForGate &&
        summaryFresh.openQuizIds.some((id) => id.trim().toLowerCase() === qidForGate.toLowerCase())
      )
        return;
    } catch {
      return;
    }
    try {
      const band = normalizePlayDifficultyLabel(snap.playDifficulty) || 'Easy';
      const room = await pvpService.createAsyncPvPRoom({
        name: snap.roomDisplayName.trim() || 'PvP',
        quizId: snap.quizId,
        playDifficulty: band,
      });
      await pvpService.completeAsyncHostQuiz(
        room.id,
        buildSoloArenaHostCompleteBody(snap),
        { pwaClient: isPwaStandalone() }
      );
      toast.success(t('quiz.soloArenaPublishSuccess', 'Room listed on the PvP Arena.'));
      soloArenaSnapshotRef.current = null;
    } catch (err: unknown) {
      const msg =
        axios.isAxiosError(err) &&
        err.response?.data &&
        typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.soloArenaPublishError', 'Could not publish to the Arena.');
      toast.error(msg);
    }
  }, [t]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const fromNav = (location.state as { soloQuizStart?: SoloQuizStartResponse } | null)?.soloQuizStart;
      const navId = fromNav?.quiz?.id;
      const quickMatchOk =
        fromNav &&
        navId &&
        (navId === quizId ||
          (quizId === SOLO_QUICK_MATCH_ROUTE_ID && navId === SOLO_QUICK_MATCH_PLACEHOLDER_QUIZ_ID));
      if (quickMatchOk) {
        setQuiz(fromNav!.quiz);
        setQuestions(fromNav!.questions);
        setWitcPlaySessionId(fromNav!.witcPlaySessionId ?? null);
        setWitcEngineBootstrapCipher(fromNav!.witcEngineBootstrapCipher ?? null);
        setStopEngineBootstrapCipher(fromNav!.stopEngineBootstrapCipher ?? null);
        setMcPlaySessionId(fromNav!.mcPlaySessionId ?? null);
        setMcEngineBootstrapCipher(fromNav!.mcEngineBootstrapCipher ?? null);
        setGeniusStart(parseGeniusPlayStart(fromNav!.genius));
        setTileMatchStart(parseTileMatchPlayStart(fromNav!.tileMatch));
        setWordConnectStart(parseWordConnectPlayStart(fromNav!.wordConnect));
        setSoloPlayDifficulty(
          (fromNav!.playDifficulty && fromNav!.playDifficulty.trim()) || fromNav!.quiz.difficulty || 'Easy'
        );
        setSoloQuickMatch(!!fromNav!.isSoloQuickMatch);
        setPlayDifficultiesPerQuestion(fromNav!.playDifficultiesPerQuestion ?? null);
        setStartTime(new Date());
        setStartErrorModal(null);
        setIsLoading(false);
        navigate('.', { replace: true, state: null });
        return;
      }

      /* Solo play must start from the lobby (QuizList hands off `soloQuizStart`). Bare /quiz/:id URLs
       * (bookmarks, external links) must not auto-start or debit a life. */
      if (!cancelled) setIsLoading(false);
      navigate('/', { replace: true });
    };

    void run();
    return () => {
      cancelled = true;
    };
    // Only quizId must re-run load. Do NOT depend on `t` or `navigate`: unstable `t` re-ran this effect mid-quiz,
    // called startSoloQuiz again, reset startTime, and made "time taken" negative on the results modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- soloQuizStart read once; location.state cleared in-run
  }, [quizId]);

  const handleBackToHome = async () => {
    await executeArenaPublishFromSoloIfNeeded();
    navigate('/', { replace: true });
  };

  const applyFreshSoloSession = (start: SoloQuizStartResponse) => {
    setQuiz(start.quiz);
    setQuestions(start.questions);
    setWitcPlaySessionId(start.witcPlaySessionId ?? null);
    setWitcEngineBootstrapCipher(start.witcEngineBootstrapCipher ?? null);
    setStopEngineBootstrapCipher(start.stopEngineBootstrapCipher ?? null);
    setMcPlaySessionId(start.mcPlaySessionId ?? null);
    setMcEngineBootstrapCipher(start.mcEngineBootstrapCipher ?? null);
    setGeniusStart(parseGeniusPlayStart(start.genius));
    setTileMatchStart(parseTileMatchPlayStart(start.tileMatch));
    setWordConnectStart(parseWordConnectPlayStart(start.wordConnect));
    setSoloPlayDifficulty(
      (start.playDifficulty && start.playDifficulty.trim()) || start.quiz.difficulty || 'Easy'
    );
    setSoloQuickMatch(!!start.isSoloQuickMatch);
    setPlayDifficultiesPerQuestion(start.playDifficultiesPerQuestion ?? null);
    setStartTime(new Date());
    soloArenaSnapshotRef.current = null;
    setArenaPublishChecked(true);
  };

  const runSoloPlayAgainCore = async () => {
    await executeArenaPublishFromSoloIfNeeded();
    if (!quiz) return;
    setSoloPlayAgainBusy(true);
    setSoloRestartLivesMessage(null);
    setSoloRestartLockedQuiz(null);
    try {
      const start = soloQuickMatch
        ? await quizService.startSoloQuickMatch()
        : await quizService.startSoloQuiz(quizId, {
            playDifficulty: soloPlayDifficulty ?? quiz.difficulty,
          });
      applyFreshSoloSession(start);
      setWitcSessionKey((k) => k + 1);
    } catch (err: unknown) {
      if (isQuizLockedApiError(err)) {
        setSoloRestartLockedQuiz(quiz);
        return;
      }
      const raw =
        axios.isAxiosError(err) && err.response?.data && typeof (err.response.data as { message?: string }).message === 'string'
          ? (err.response.data as { message: string }).message
          : t('quiz.loadFailed', 'Failed to load quiz');
      setSoloRestartLivesMessage(isInsufficientLivesMessage(raw) ? '' : translateLivesOrRaw(t, raw));
    } finally {
      setSoloPlayAgainBusy(false);
    }
  };

  const handleSoloPlayAgain = async () => {
    if (!quiz) return;
    if (isQuizCareerLocked(quiz)) {
      setSoloRestartLockedQuiz(quiz);
      return;
    }
    try {
      const inv = await inventoryService.getMine();
      const u = await authService.getProfile();
      if (shouldPromptXpPotionReminder(u, inv.slots)) {
        setSoloPlayAgainXpGateOpen(true);
        return;
      }
      if (
        shouldPromptHealthPotionReminder(u) &&
        inventorySlotsHaveUnusedHealthPotion(inv.slots)
      ) {
        setSoloPlayAgainHealthGateOpen(true);
        return;
      }
    } catch {
      /* seguir sem gate */
    }
    await runSoloPlayAgainCore();
  };

  /** Esconde resultados no filho (WITC / partida rápida mista) enquanto aviso de poção está aberto */
  const suppressPlayAgainResultsModal = soloPlayAgainXpGateOpen || soloPlayAgainHealthGateOpen;

  const playAgainPotionReminderModals = (
    <>
      <XpPotionReminderModal
        show={soloPlayAgainXpGateOpen}
        onHide={() => setSoloPlayAgainXpGateOpen(false)}
        onGoToInventory={() => {
          setSoloPlayAgainXpGateOpen(false);
          navigate('/inventory');
        }}
        onContinueWithoutBonus={() => {
          void (async () => {
            try {
              const inv = await inventoryService.getMine();
              const u = await authService.getProfile();
              if (
                shouldPromptHealthPotionReminder(u) &&
                inventorySlotsHaveUnusedHealthPotion(inv.slots)
              ) {
                setSoloPlayAgainXpGateOpen(false);
                setSoloPlayAgainHealthGateOpen(true);
                return;
              }
            } catch {
              /* seguir */
            }
            await runSoloPlayAgainCore();
            setSoloPlayAgainXpGateOpen(false);
          })();
        }}
      />
      <HealthPotionReminderModal
        show={soloPlayAgainHealthGateOpen}
        onHide={() => setSoloPlayAgainHealthGateOpen(false)}
        onGoToInventory={() => {
          setSoloPlayAgainHealthGateOpen(false);
          navigate('/inventory');
        }}
        onContinueWithoutUsing={() => {
          void (async () => {
            await runSoloPlayAgainCore();
            setSoloPlayAgainHealthGateOpen(false);
          })();
        }}
      />
    </>
  );

  if (isLoading) {
    return (
      <div className="quiz-finish-saving-overlay" role="status" aria-live="polite" aria-busy="true">
        <div className="quiz-finish-saving-overlay__inner">
          <Spinner animation="border" role="presentation" />
          <span className="text-white mt-2 small">{t('quiz.loadingQuiz', 'Loading game...')}</span>
        </div>
      </div>
    );
  }

  if (startErrorModal !== null) {
    return (
      <>
        <LivesGateModal
          show
          message={startErrorModal || undefined}
          onHide={() => {
            setStartErrorModal(null);
            navigate('/');
          }}
          onVipNavigate={() => {
            setStartErrorModal(null);
            navigate('/vip');
          }}
        />
        <div className="quiz-loading" style={{ minHeight: '40vh' }} aria-hidden />
      </>
    );
  }

  const isMixedQuickMatch =
    soloQuickMatch &&
    quiz &&
    String(quiz.format ?? '').toLowerCase() === 'mixed';

  if (!isLoading && quiz && isMixedQuickMatch && questions.length > 0 && startTime) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={questions.length}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <QuickMatchMixedPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          questions={questions}
          startTime={startTime}
          witcPlaySessionId={witcPlaySessionId}
          playDifficultiesPerQuestion={playDifficultiesPerQuestion}
          soloPlayDifficulty={soloPlayDifficulty}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          suppressResultsModal={suppressPlayAgainResultsModal}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (
    !isLoading &&
    quiz &&
    (quiz.format === 'Witc' || quiz.format === 'witc') &&
    questions.length > 0 &&
    startTime
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={questions.length}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <WitcQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          questions={questions}
          startTime={startTime}
          witcPlaySessionId={witcPlaySessionId}
          witcEngineBootstrapCipher={witcEngineBootstrapCipher}
          playDifficulty={soloPlayDifficulty ?? quiz.difficulty}
          playDifficultyPerQuestion={playDifficultiesPerQuestion}
          soloQuickMatch={soloQuickMatch}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          suppressResultsModal={suppressPlayAgainResultsModal}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (
    !isLoading &&
    quiz &&
    isGeniusQuizFormat(quiz) &&
    geniusStart &&
    startTime
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={GENIUS_TOTAL_ROUNDS}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <GeniusQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          genius={geniusStart}
          startTime={startTime}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          suppressResultsModal={suppressPlayAgainResultsModal}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
          onExitHome={async () => {
            await executeArenaPublishFromSoloIfNeeded();
            navigate('/', { replace: true });
          }}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (
    !isLoading &&
    quiz &&
    isTileMatchQuizFormat(quiz) &&
    tileMatchStart &&
    startTime
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={TILE_MATCH_TOTAL_ROUNDS}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <TileMatchQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          tileMatch={tileMatchStart}
          startTime={startTime}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (
    !isLoading &&
    quiz &&
    isWordConnectQuizFormat(quiz) &&
    wordConnectStart &&
    startTime
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={wordConnectStart.activeWordCount}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <WordConnectQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          wordConnect={wordConnectStart}
          startTime={startTime}
          playDifficulty={soloPlayDifficulty ?? quiz.difficulty ?? 'Medium'}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (
    !isLoading &&
    quiz &&
    (quiz.format === 'Stop' || quiz.format === 'stop') &&
    questions.length > 0 &&
    startTime
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={questions.length}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <StopQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          questions={questions}
          startTime={startTime}
          stopPlaySessionId={witcPlaySessionId}
          stopEngineBootstrapCipher={stopEngineBootstrapCipher}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          suppressResultsModal={suppressPlayAgainResultsModal}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  const isMcQuiz =
    quiz &&
    (quiz.format === 'MultipleChoice' || quiz.format === 'multiplechoice');

  if (
    !isLoading &&
    quiz &&
    isMcQuiz &&
    questions.length > 0 &&
    startTime &&
    mcEngineBootstrapCipher
  ) {
    return (
      <>
        <QuizPlayerSeo
          quiz={quiz}
          quizId={quizId}
          questionCount={questions.length}
          language={i18n.language}
        />
        <LivesGateModal
          show={soloRestartLivesMessage !== null}
          message={soloRestartLivesMessage || undefined}
          onHide={() => setSoloRestartLivesMessage(null)}
          onVipNavigate={() => {
            setSoloRestartLivesMessage(null);
            navigate('/vip');
          }}
        />
        <QuizLockedModal
          show={soloRestartLockedQuiz !== null}
          onHide={() => setSoloRestartLockedQuiz(null)}
          quizTitle={
            soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : ''
          }
          onGoCareer={() => {
            const caption = soloRestartLockedQuiz
              ? getQuizFullDisplayCaption(soloRestartLockedQuiz, i18n.language)
              : '';
            setSoloRestartLockedQuiz(null);
            navigate(careerHomePathWithSearch(caption));
          }}
        />
        <McQuizPlayer
          key={witcSessionKey}
          quizId={quizId}
          quiz={quiz}
          questions={questions}
          startTime={startTime}
          mcPlaySessionId={mcPlaySessionId}
          mcEngineBootstrapCipher={mcEngineBootstrapCipher}
          playDifficulty={soloPlayDifficulty ?? quiz.difficulty}
          playDifficultyPerQuestion={playDifficultiesPerQuestion}
          soloQuickMatch={soloQuickMatch}
          onPlayAgain={handleSoloPlayAgain}
          playAgainLoading={soloPlayAgainBusy}
          suppressResultsModal={suppressPlayAgainResultsModal}
          arenaPublishSnapshotRef={soloArenaSnapshotRef}
          arenaPublishChecked={arenaPublishChecked}
          onArenaPublishCheckedChange={setArenaPublishChecked}
          arenaPublishCheckedRef={arenaPublishCheckedRef}
        />
        {playAgainPotionReminderModals}
      </>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <div className="quiz-error">
        <Alert variant="warning">
          <Alert.Heading>{t('quiz.quizNotFound', 'Quiz not found')}</Alert.Heading>
          <p>{t('quiz.quizNotAvailable', 'This quiz is not available or has no questions.')}</p>
          <Button variant="outline-warning" onClick={() => void handleBackToHome()}>
            {t('quiz.backToHome', 'Back to Home')}
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="quiz-error">
      <Alert variant="warning">
        <Alert.Heading>{t('quiz.quizNotFound', 'Quiz not found')}</Alert.Heading>
        <p>{t('quiz.unsupportedFormat', 'This quiz format is not supported in the player.')}</p>
        <Button variant="outline-warning" onClick={() => void handleBackToHome()}>
          {t('quiz.backToHome', 'Back to Home')}
        </Button>
      </Alert>
    </div>
  );
};

export default QuizPlayer; 